import * as React from "react";
import { Button as ShadcnButton } from "../button";
import { cn } from "@/lib/utils";
import { Spinner } from "../spinner";

// Map Hero UI variant to shadcn variant
const VARIANT_MAP: Record<
  string,
  "default" | "outline" | "ghost" | "secondary"
> = {
  bordered: "outline",
  light: "ghost",
  flat: "ghost",
  faded: "ghost",
  shadow: "secondary",
  ghost: "ghost",
  solid: "default",
};

// Map Hero UI size to shadcn size
const SIZE_MAP: Record<string, "default" | "sm" | "lg"> = {
  sm: "sm",
  md: "default",
  lg: "lg",
};

// Map radius to Tailwind classes
const RADIUS_MAP: Record<string, string> = {
  none: "rounded-none",
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  xl: "rounded-xl",
  "2xl": "rounded-2xl",
  full: "rounded-full",
};

// Map color to appropriate classes
const COLOR_VARIANT_MAP: Record<string, Record<string, string>> = {
  primary: {
    solid: "bg-primary text-white",
    bordered: "border-primary text-primary",
  },
  secondary: {
    solid: "bg-secondary-light text-contrast-light",
    bordered: "border-secondary-light text-secondary-light",
  },
  success: {
    solid: "bg-green-500 text-white",
    bordered: "border-green-500 text-green-500",
  },
  warning: {
    solid: "bg-yellow-500 text-white",
    bordered: "border-yellow-500 text-yellow-500",
  },
  danger: {
    solid: "bg-danger text-white",
    bordered: "border-danger text-danger",
  },
  default: {
    solid: "bg-gray-200 text-muted-light",
    bordered: "border-muted-light",
  },
};

export interface ButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "onClick" | "disabled" | "size"
> {
  onPress?: () => void;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  variant?:
    | "solid"
    | "bordered"
    | "light"
    | "flat"
    | "faded"
    | "shadow"
    | "ghost";
  color?:
    | "default"
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "danger";
  size?: "sm" | "md" | "lg";
  radius?: "none" | "sm" | "md" | "lg" | "xl" | "2xl" | "full";
  isLoading?: boolean;
  spinner?: React.ReactNode;
  isDisabled?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  isIconOnly?: boolean;
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  className?: string;
  classNames?: {
    base?: string;
  };
  children?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    const {
      onPress,
      onClick,
      variant = "solid",
      color = "default",
      size = "md",
      radius = "2xl",
      isLoading,
      spinner,
      isDisabled,
      disabled,
      fullWidth,
      isIconOnly,
      startContent,
      endContent,
      className,
      classNames,
      children,
      ...restProps
    } = props;
    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if (onPress) {
          e.preventDefault();
          onPress();
        }
        onClick?.(e);
      },
      [onPress, onClick]
    );

    // Map Hero UI variant to shadcn variant
    const shadcnVariant = React.useMemo(
      () => VARIANT_MAP[variant] || "default",
      [variant]
    );

    // Map Hero UI size to shadcn size
    const shadcnSize = React.useMemo(() => SIZE_MAP[size] || "default", [size]);

    // Map radius to Tailwind classes
    const radiusClass = React.useMemo(() => RADIUS_MAP[radius] || "", [radius]);

    // Map color to appropriate classes
    // Only apply colorClass when color is explicitly set and not "default"
    const colorClass = React.useMemo(() => {
      if (color === "default") {
        return "";
      }
      const colorMap = COLOR_VARIANT_MAP[color];
      return colorMap?.[variant] || "";
    }, [color, variant]);

    // Determine if we should show custom spinner or default loading
    const showCustomSpinner = isLoading && spinner;
    const showDefaultLoading = isLoading && !spinner;
    const isButtonDisabled = disabled ?? isDisabled ?? isLoading;

    // Add border-1.5 only for variants that need borders
    const shouldHaveBorder = variant === "bordered";

    // Spinner color based on variant
    const spinnerColor =
      shouldHaveBorder || variant === "light" || variant === "ghost"
        ? "primary"
        : "white";

    return (
      <ShadcnButton
        ref={ref}
        onClick={handleClick}
        variant={shadcnVariant}
        size={shadcnSize}
        disabled={isButtonDisabled}
        isLoading={showDefaultLoading}
        className={cn(
          shouldHaveBorder && "border-1.5",
          shouldHaveBorder && !isButtonDisabled && "hover:bg-white",
          isButtonDisabled ? "cursor-not-allowed" : "cursor-pointer",
          radiusClass,
          colorClass,
          fullWidth && "w-full",
          isIconOnly && "aspect-square p-0 flex items-center justify-center",
          startContent && "flex items-center",
          endContent && "flex items-center",
          "gap-2",
          classNames?.base,
          className
        )}
        {...restProps}
      >
        {showCustomSpinner && (
          <span className="flex items-center">{spinner}</span>
        )}
        {showDefaultLoading && <Spinner size="sm" color={spinnerColor} />}
        {startContent && (
          <span className="flex items-center shrink-0">{startContent}</span>
        )}
        {children}
        {endContent && (
          <span className="flex items-center shrink-0">{endContent}</span>
        )}
      </ShadcnButton>
    );
  }
);
Button.displayName = "Button";

export { Button };
